/**
 * `role_mapping_cache` store — caches the customer-set internal→external role
 * ID mappings so the terminal can resolve `externalRoleId` from a `roleId`
 * without a network call, including offline (API-4674).
 *
 * Hydrated alongside the LDR cache (hydrate-then-revalidate): read the cache
 * first (works offline); when online, refresh in the background and overwrite
 * via `setRoleMappings`. Role mappings change rarely (a one-time admin action),
 * so a per-hydrate snapshot is fresh enough (decision 2026-07-27).
 *
 * Both operations fail soft (log + resolve) — losing this cache only means an
 * `externalRoleId` is omitted from a punch payload, never a lost punch and
 * never a wrong id.
 */

import { ROLE_MAPPING_CACHE_STORE, getValue, setValue } from './offlineDb';
import { OFFLINE_LIMIT_DAYS } from './offlineLimit';
import type { RoleMappings } from '@src/api/roleMappingApi';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * Freshness window for a cached role-mapping snapshot. The cache is only ever
 * read while offline (online punches resolve `externalRoleId` from the server),
 * and the terminal may operate offline for at most `OFFLINE_LIMIT_DAYS` before
 * lockout. A snapshot older than that window therefore cannot belong to a valid
 * offline session, so it is treated as untrustworthy — better to omit
 * `externalRoleId` than to emit a stale, possibly-remapped id (API-4674).
 */
export const ROLE_MAPPING_MAX_AGE_MS = OFFLINE_LIMIT_DAYS * MS_PER_DAY;

/**
 * Whether a cached snapshot's `updatedAt` is within the freshness window.
 * Rejects invalid/unparseable timestamps and future-dated entries (a
 * same-device clock anomaly) so a stale or bogus snapshot never yields an id.
 */
function isFresh(updatedAt: string, now: Date): boolean {
    const updatedMs = Date.parse(updatedAt);
    if (Number.isNaN(updatedMs)) {
        return false;
    }
    const age = now.getTime() - updatedMs;
    return age >= 0 && age <= ROLE_MAPPING_MAX_AGE_MS;
}

export interface RoleMappingCache {
    key: string; // `${companyId}:${locationId}`
    mappings: RoleMappings; // internalRoleId -> externalRoleId
    updatedAt: string; // ISO 8601 — when last refreshed
}

function cacheKey(companyId: number, locationId: number): string {
    return `${companyId}:${locationId}`;
}

export async function setRoleMappings(
    companyId: number,
    locationId: number,
    mappings: RoleMappings
): Promise<void> {
    const key = cacheKey(companyId, locationId);
    const entry: RoleMappingCache = {
        key,
        mappings,
        updatedAt: new Date().toISOString()
    };
    try {
        await setValue(ROLE_MAPPING_CACHE_STORE, key, entry);
    } catch (err) {
        console.error(
            '[roleMappingCache] Failed to persist role mapping cache',
            err
        );
    }
}

export async function getRoleMappings(
    companyId: number,
    locationId: number
): Promise<RoleMappingCache | null> {
    try {
        const entry = await getValue<RoleMappingCache>(
            ROLE_MAPPING_CACHE_STORE,
            cacheKey(companyId, locationId)
        );
        return entry ?? null;
    } catch (err) {
        console.error(
            '[roleMappingCache] Failed to read role mapping cache',
            err
        );
        return null;
    }
}

/**
 * Resolves an internal `roleId` to its partner `externalRoleId` from the
 * cache, or `null` when the mapping is missing or the snapshot is stale
 * (older than `ROLE_MAPPING_MAX_AGE_MS`, or an invalid timestamp). Fail-soft:
 * never throws, so a punch is never blocked and a stale/wrong id is never
 * emitted (graceful degradation, decision 2026-07-27). Ready for the offline
 * `punch_success` payload once that emission lands. `now` is injectable for
 * testing.
 */
export async function resolveExternalRoleId(
    companyId: number,
    locationId: number,
    roleId: number,
    now: Date = new Date()
): Promise<string | null> {
    const cache = await getRoleMappings(companyId, locationId);
    if (!cache || !isFresh(cache.updatedAt, now)) {
        return null;
    }
    return cache.mappings[roleId] ?? null;
}
